import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface ProgressoBadgeProps {
  camposObrigatorios: string[];
  camposPreenchidos: string[];
  isCancelado?: boolean;
  isAtivo?: boolean;
  foiRecuperado?: boolean;
}

export function ProgressoBadge({ 
  camposObrigatorios, 
  camposPreenchidos,
  isCancelado = false,
  isAtivo = false,
  foiRecuperado = false
}: ProgressoBadgeProps) {
  // ✅ Usar Sets para deduplicar e calcular interseção corretamente
  const requiredSet = new Set(camposObrigatorios);
  const filledSet = new Set(camposPreenchidos);
  
  // Apenas contar preenchidos que são também obrigatórios
  const filledRequiredSet = new Set([...filledSet].filter(c => requiredSet.has(c)));
  
  const total = requiredSet.size;
  const preenchidos = filledRequiredSet.size;
  
  // ✅ Clamp em 100% para nunca mostrar mais que 100%
  const percentual = total > 0 ? Math.min(100, Math.round((preenchidos / total) * 100)) : 100;
  
  // Campos faltando baseado nos Sets
  const camposFaltando = [...requiredSet].filter(c => !filledRequiredSet.has(c));

  // Estados especiais (ordem de prioridade)
  // 🔵 AZUL: Item foi cancelado mas foi recuperado (PRIORIDADE 1)
  if (foiRecuperado) {
    return (
      <div className="flex items-center gap-2">
        <Progress value={100} className="h-1.5 flex-1" indicatorClassName="bg-muted-foreground/40" />
        <Badge className="whitespace-nowrap border-border bg-accent text-[10px] font-bold text-muted-foreground">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Recuperado
        </Badge>
      </div>
    );
  }

  // 🔴 VERMELHO: Item está cancelado atualmente (PRIORIDADE 2)
  if (isCancelado) {
    return (
      <div className="flex items-center gap-2">
        <Progress value={100} className="h-1.5 flex-1" indicatorClassName="bg-destructive" />
        <Badge variant="destructive" className="text-xs whitespace-nowrap">
          <AlertCircle className="w-3 h-3 mr-1" />
          Cancelado
        </Badge>
      </div>
    );
  }

  // 🟢 VERDE: Item está ativo
  if (isAtivo) {
    return (
      <div className="flex items-center gap-2">
        <Progress value={100} className="h-1.5 flex-1" indicatorClassName="bg-success" />
        <Badge variant="success" className="text-xs whitespace-nowrap">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Ativo
        </Badge>
      </div>
    );
  }

  // Estado normal - mostrar progresso
  /* §8 do punch: o estado INTERMEDIÁRIO é neutro. Estava `bg-primary` (bordô
     cheio), que lê como marca/desfecho — e o azul das outras cópias também não
     serve: azul não existe na paleta Prime. Verde e vermelho ficam porque são
     desfecho de verdade. */
  const cor = percentual === 100 ? 'bg-success' : 'bg-muted-foreground/40';
  const label = percentual === 100 ? 'Pronto para envio' : `${percentual}% completo`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 w-full cursor-help">
            <Progress 
              value={percentual} 
              className="h-1.5 flex-1"
              indicatorClassName={cor}
            />
            <Badge 
              variant={percentual === 100 ? "success" : "secondary"}
              className="text-xs whitespace-nowrap"
            >
              {percentual === 100 ? (
                <CheckCircle2 className="w-3 h-3 mr-1" />
              ) : (
                <AlertCircle className="w-3 h-3 mr-1" />
              )}
              {label}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            <div className="font-semibold">
              {preenchidos} de {total} campos obrigatórios
            </div>
            {camposFaltando.length > 0 && (
              <div className="text-xs">
                <div className="font-medium mb-1">Campos faltando:</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {camposFaltando.map(campo => (
                    <li key={campo}>{campo}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
